// Schrift-Erkennung für nutzer-verfasste Texte (Skript, Block-Text, Infotext, Titel).
//
// Die Schrift selbst wählt der Browser pro Zeichen über den Font-Stack mit
// unicode-range (src/styles/fonts.css) — dafür braucht es keine Erkennung.
// `detectScript` liefert die ÜBERWIEGENDE Schrift eines Texts für das, was
// CSS nicht von allein kann: typografische Anpassungen je Schrift
// ([data-script] in global.css), Vietnamesisch komplett in Be Vietnam Pro
// (Poppins hat keine vietnamesischen Glyphen) und das Ausblenden des
// Satz-Splits bei Thai. Überwiegend deshalb, weil in einem Thai- oder
// Arabisch-Skript auch lateinische Markennamen stehen.
// Spec: docs/superpowers/specs/2026-09-23-script-support-design.md

export type TextScript = 'latin' | 'vietnamese' | 'arabic' | 'thai' | 'bengali' | 'cjk' | 'other';

const SCRIPTS: [Exclude<TextScript, 'latin' | 'vietnamese' | 'other'>, RegExp][] = [
  ['arabic', /\p{Script=Arabic}/u],
  ['thai', /\p{Script=Thai}/u],
  ['bengali', /\p{Script=Bengali}/u],
  ['cjk', /[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Hangul}]/u],
];
const LATIN = /\p{Script=Latin}/u;
const LETTER = /\p{L}/u;
// Eindeutig vietnamesisch: ơ ư đ + Latin Extended Additional (ạ ả ấ … ỹ).
// â ê ô ă kommen auch in pt/ro vor und zählen deshalb nicht.
const VIETNAMESE = /[ơưđƠƯĐẠ-ỹ]/u;

export function detectScript(text: string | null | undefined): TextScript {
  if (!text) return 'latin';
  const counts = new Map<TextScript, number>();
  let viet = false;
  for (const ch of text) {
    if (!LETTER.test(ch)) continue;
    let script: TextScript = 'other';
    if (LATIN.test(ch)) {
      script = 'latin';
      if (VIETNAMESE.test(ch)) viet = true;
    } else {
      for (const [name, re] of SCRIPTS) {
        if (re.test(ch)) { script = name; break; }
      }
    }
    counts.set(script, (counts.get(script) ?? 0) + 1);
  }
  if (counts.size === 0) return 'latin';
  let best: TextScript = 'latin';
  let max = 0;
  for (const [script, n] of counts) {
    if (n > max) { best = script; max = n; }
  }
  return best === 'latin' && viet ? 'vietnamese' : best;
}

// Die CJK-Schriftregeln (fonts-cjk.css, ~150 KB unicode-range-Listen) lädt
// erst der erste chinesische Text nach — die übrigen Sprachen zahlen nichts.
let cjkFontsRequested = false;
function requestCjkFonts(): void {
  if (cjkFontsRequested) return;
  cjkFontsRequested = true;
  void import('@/styles/fonts-cjk.css');
}

/** Attribute für Elemente mit nutzer-verfasstem Text: Schreibrichtung aus dem
 *  Inhalt (`dir="auto"`, erstes starkes Zeichen) + Schrift für die CSS-Regeln. */
export function scriptAttrs(text: string | null | undefined): { dir: 'auto'; 'data-script': TextScript } {
  const script = detectScript(text);
  if (script === 'cjk') requestCjkFonts();
  return { dir: 'auto', 'data-script': script };
}
